import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import * as userApi from 'apis/restaurant-api/merchant-admin/user/api';
import { isDefined } from 'common/utils';
import { usePermissions } from 'common/wolt-permissions';
import { hasPermissions } from 'common/wolt-permissions';
import { useSelectionData } from 'modules/shared-menu/hooks/useSelectionData';

const useLatestMenuVersions = () => {
  const { categories, offers, options } = useSelectionData();

  return useMemo(() => {
    const [first, second, third, fourth, fifth] = [
      ...categories.map(item => item.version),
      ...offers.map(offer => offer.version),
      ...options.map(option => option.version),
    ].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
    return [first, second, third, fourth, fifth].filter(isDefined);
  }, [categories, offers, options]);
};

// Initialize a cache using a Map to store resolved author names
const authorNameCache = new Map();

export const resolveAuthorName = async (authorId: string) => {
  // Check if the author's name for the given ID is already cached
  if (authorNameCache.has(authorId)) {
    // Return the cached author name if it exists
    return authorNameCache.get(authorId);
  }
  const authorIsUser = authorIsValidUserId(authorId);

  if (authorIsUser === false) {
    return authorId;
  }

  try {
    const { users } = await userApi.searchUsers(authorId);
    const name = users.length === 0 ? authorId : `${users[0].name.first_name} ${users[0].name.last_name}`;
    // Cache the resolved author name for future use
    authorNameCache.set(authorId, name);
    return name;
  } catch (error) {
    if (error.response?.status === 403) {
      return authorId;
    }
    throw error;
  }
};

export const authorIsValidUserId = (author: string) => {
  // Mongo ObjectIds are 24 characters long
  if (author.length !== 24) {
    return false;
  }

  // Check if all characters in the string are hexadecimal
  const hexRegex = /^[0-9a-fA-F]{24}$/;
  return hexRegex.test(author);
};

export const useListingManagerLastEdited = () => {
  const latestFiveVersions = useLatestMenuVersions();
  const permissions = usePermissions();
  const { selection } = useSelectionData();

  const { data, isLoading, refetch } = useQuery(
    ['resolve-authors', selection.id],
    async () => {
      // checking if user has permission to get user info (external users don't have, so only showing the edit data and time for them)
      const hasPermissionToSearchUsers = hasPermissions([{ resource: 'user', operations: ['r'] }], permissions);

      if (!hasPermissionToSearchUsers) {
        const allAuthors = latestFiveVersions.map(version => {
          const date = Intl.DateTimeFormat(undefined, { month: 'numeric', day: 'numeric' }).format(
            new Date(version.updated_at).getTime(),
          );

          const time = Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: 'numeric' }).format(
            new Date(version.updated_at).getTime(),
          );

          return { name: null, date, time };
        });
        return allAuthors;
      }

      const allAuthors = await Promise.all(
        latestFiveVersions.map(async version => {
          const name = hasPermissionToSearchUsers ? await resolveAuthorName(version.author) : null;
          const date = Intl.DateTimeFormat(undefined, { month: 'numeric', day: 'numeric' }).format(
            new Date(version.updated_at).getTime(),
          );

          const time = Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: 'numeric' }).format(
            new Date(version.updated_at).getTime(),
          );

          return {
            name,
            date,
            time,
          };
        }),
      ).then(results => results);

      return allAuthors;
    },
    {
      staleTime: Infinity,
      refetchOnWindowFocus: false,
    },
  );

  useEffect(() => {
    refetch();
  }, [latestFiveVersions, refetch]);

  return { allAuthors: data, resolvingAuthors: isLoading };
};
